import type { CaptureResult } from "posthog-js";

export type ExceptionProperties = Record<string, unknown>;

export interface ExceptionSink {
    captureException(error: unknown, properties: ExceptionProperties): void;
    addExceptionStep(message: string, properties: ExceptionProperties): void;
}

let sink: ExceptionSink | null = null;

export const setExceptionSink = (next: ExceptionSink | null): void => {
    sink = next;
};

export const captureExceptionToSink = (
    error: unknown,
    properties: ExceptionProperties,
): void => {
    sink?.captureException(error, properties);
};

export const recordExceptionStep = (
    message: string,
    properties: ExceptionProperties,
): void => {
    sink?.addExceptionStep(message, properties);
};

const contextPropertyNames: Record<string, string> = {
    correlationId: "correlation_id",
    tenantId: "tenant_id",
    userId: "user_id",
    serverVersion: "server_version",
    query: "graphql_query",
};

const toPropertyValue = (key: string, value: unknown): unknown =>
    key === "query" ? String(value) : value;

export const toExceptionProperties = (
    context: Record<string, unknown>,
): ExceptionProperties =>
    Object.fromEntries(
        Object.entries(context).map(([key, value]) => [
            contextPropertyNames[key] ?? key,
            toPropertyValue(key, value),
        ]),
    );

let exceptionContext: ExceptionProperties = {};

export const mergeExceptionContext = (context: Record<string, unknown>): void => {
    exceptionContext = { ...exceptionContext, ...toExceptionProperties(context) };
};

export const getExceptionContext = (): ExceptionProperties => ({ ...exceptionContext });

export const resetExceptionContextForTesting = (): void => {
    exceptionContext = {};
};

export const enrichExceptionEvent = (
    event: CaptureResult | null,
): CaptureResult | null => {
    if (!event || event.event !== "$exception") {
        return event;
    }
    return { ...event, properties: { ...exceptionContext, ...event.properties } };
};
